import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import type {IssueLockMutation, LockReason} from '@github-ui/server-persisted-queries/types/IssueLock'
import type {IssueUnlockMutation} from '@github-ui/server-persisted-queries/types/IssueUnlock'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

type ConversationLockOptions = {
  issueId: string
  nameWithOwner: string
  issueNumber: number
  onError?: (error: Error) => void
  onCompleted?: () => void
}

type LockedIssueRecord = Extract<
  NonNullable<IssueLockMutation['lockLockable']>['lockedRecord'],
  {readonly locked: boolean}
>

type GraphQLResponse<TData> = {
  readonly data?: TData
  readonly errors?: ReadonlyArray<{readonly message?: string}>
}

const LOCK_ERROR = 'Failed to lock the conversation'
const UNLOCK_ERROR = 'Failed to unlock the conversation'

async function readGraphQLResponse<TData>(response: Response, fallbackMessage: string): Promise<TData> {
  if (!response.ok) {
    throw new Error(`${fallbackMessage} (${response.status})`)
  }

  const json = (await response.json()) as GraphQLResponse<TData> | null
  if (!json?.data) throw new Error(json?.errors?.[0]?.message ?? fallbackMessage)

  return json.data
}

export function useLockConversation({
  issueId,
  nameWithOwner,
  issueNumber,
  onError,
  onCompleted,
}: ConversationLockOptions) {
  const {mutate} = useEntityMutation<LockedIssueRecord>({
    scope: {id: `conversation-lock:${issueId}`},
    mutationFn: async operation => {
      let response: Response
      try {
        response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/conversation_lock`, {
          method: 'PUT',
          body: {lockReason: operation.fields.lockReason},
        })
      } catch (error) {
        throw new Error(LOCK_ERROR, {cause: error})
      }

      const data = await readGraphQLResponse<IssueLockMutation>(response, LOCK_ERROR)
      const record = data.lockLockable?.lockedRecord
      if (!record || !('locked' in record) || record.id !== issueId) throw new Error(LOCK_ERROR)
      return record
    },
    getConfirmedOperation: record => ({
      type: 'setFields',
      id: record.id,
      fields: {locked: record.locked},
    }),
    onError: error => onError?.(error),
    onSuccess: () => onCompleted?.(),
  })

  return useCallback(
    (lockReason: LockReason | null) => {
      mutate({
        operation: {
          type: 'setFields',
          id: issueId,
          fields: {locked: true, lockReason},
        },
      })
    },
    [issueId, mutate],
  )
}

export function useUnlockConversation({
  issueId,
  nameWithOwner,
  issueNumber,
  onError,
  onCompleted,
}: ConversationLockOptions) {
  const {mutate} = useEntityMutation<LockedIssueRecord>({
    scope: {id: `conversation-lock:${issueId}`},
    mutationFn: async () => {
      let response: Response
      try {
        response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/conversation_lock`, {
          method: 'DELETE',
        })
      } catch (error) {
        throw new Error(UNLOCK_ERROR, {cause: error})
      }

      const data = await readGraphQLResponse<IssueUnlockMutation>(response, UNLOCK_ERROR)
      const record = data.unlockLockable?.unlockedRecord
      if (!record || !('locked' in record) || record.id !== issueId) throw new Error(UNLOCK_ERROR)
      return record
    },
    getConfirmedOperation: record => ({
      type: 'setFields',
      id: record.id,
      fields: {locked: record.locked},
    }),
    onError: error => onError?.(error),
    onSuccess: () => onCompleted?.(),
  })

  return useCallback(() => {
    mutate({
      operation: {
        type: 'setFields',
        id: issueId,
        fields: {locked: false},
      },
    })
  }, [issueId, mutate])
}
